/* Problems panel (W2-12a checker-runtime; DESIGN.md §4.18). Design tokens only (src/ui/styles/tokens.css). */

.panel {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--color-surface-1);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.group {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--color-text-subtle);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);
}

.chip:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.chip:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.chipOn {
  border-color: var(--color-border-strong);
  background: var(--color-surface-3);
  color: var(--color-text);
}

.chip[data-severity='fatal'] svg {
  color: var(--color-severity-fatal);
}
.chip[data-severity='error'] svg {
  color: var(--color-severity-error);
}
.chip[data-severity='warning'] svg {
  color: var(--color-severity-warning);
}
.chip[data-severity='tip'] svg {
  color: var(--color-severity-tip);
}

.search {
  flex: 1 1 140px;
  min-width: 120px;
}

.statusGroup {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  white-space: nowrap;
}

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-severity-fatal-muted);
  color: var(--color-text);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.banner svg {
  flex: none;
  color: var(--color-severity-fatal);
}

.notice {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.notice svg {
  flex: none;
  color: var(--color-severity-warning);
}

/* The body stacks the count, the scrolling list and the ignored section; only the list scrolls. */
.body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.state {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.count {
  flex: none;
  margin: 0;
  padding: var(--space-1) var(--space-3);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

/* Scroll container of the virtualised rows (virtualRows.ts): rows are positioned at their offsets. */
.scroller {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-anchor: none;
}

.list {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
}

.vrow {
  position: absolute;
  right: 0;
  left: 0;
  box-sizing: border-box;
}

.tipsRow {
  display: flex;
  padding: var(--space-1) var(--space-3) var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  border-left: 3px solid transparent;
}

.tipsToggle {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-2);
  min-width: 0;
  padding: var(--space-1);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tipsToggle:hover {
  background: var(--color-surface-hover);
}

.tipsToggle:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.tipsLabel {
  font-weight: var(--weight-medium);
}

.tipsHint {
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3) var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  border-left: 3px solid transparent;
}

.row:hover {
  background: var(--color-surface-hover);
}

.current {
  background: var(--color-selection);
  border-left-color: var(--color-accent);
}

.current:hover {
  background: var(--color-selection);
}

.resolving {
  opacity: 0.6;
}

.rowMain {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: var(--space-2);
  flex: 1 1 auto;
  min-width: 0;
  padding: var(--space-1);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.rowMain:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.sev {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding-top: 1px;
}

.sev[data-severity='fatal'] svg {
  color: var(--color-severity-fatal);
}
.sev[data-severity='error'] svg {
  color: var(--color-severity-error);
}
.sev[data-severity='warning'] svg {
  color: var(--color-severity-warning);
}
.sev[data-severity='tip'] svg {
  color: var(--color-severity-tip);
}

/* The severity word stays in the accessibility tree and shows on wide panels; the icon shape differs per severity. */
.sevWord {
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  min-width: 5.5em;
}

.text {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  min-width: 0;
}

.message {
  color: var(--color-text);
  line-height: var(--leading-tight);
  overflow-wrap: anywhere;
}

.why {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  overflow-wrap: anywhere;
}

.fixing {
  color: var(--color-success);
  font-size: var(--text-xs);
}

.where {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-0);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  white-space: nowrap;
}

.rule {
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
}

.actions {
  display: inline-flex;
  flex: none;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-1);
  max-width: 50%;
  padding-top: var(--space-0);
}

.noMatch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-6) var(--space-4);
  color: var(--color-text-muted);
  text-align: center;
}

.noMatchTitle {
  margin: 0;
  color: var(--color-text);
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
}

.noMatchBody {
  margin: 0;
  font-size: var(--text-sm);
}

.ignored {
  flex: none;
  max-height: 40%;
  overflow-y: auto;
  border-top: var(--border-width) solid var(--color-divider);
  padding: var(--space-1) var(--space-3);
}

.ignoredList {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ignoredRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-1) 0;
}

.ignoredText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-wrap: anywhere;
}

.ignoredKey {
  color: var(--color-text-subtle);
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
}

@container (max-width: 520px) {
  .sevWord {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}

/*
 * Narrow and default docks (UX-093): the row stacks so the message gets the whole width: severity icon and message
 * on top, then the place (rule id and line, beat) and the actions below the message.
 */
@container (max-width: 680px) {
  .row {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .rowMain {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      'sev text'
      '. where';
    row-gap: var(--space-1);
  }

  .rowMain > .sev {
    grid-area: sev;
  }

  .rowMain > .text {
    grid-area: text;
  }

  .where {
    grid-area: where;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
    white-space: normal;
  }

  .actions {
    justify-content: flex-start;
    max-width: none;
    padding: 0 0 var(--space-1) calc(16px + var(--space-3));
  }
}

@media (prefers-reduced-motion: reduce) {
  .chip {
    transition: none;
  }
}
